import { forwardRef } from 'react';

import { Card, type CardProps, useToken } from '@vimeo/bokeh';

import { type BaseVideoCardProps } from './types';

/**
 * Card come with a transform value, need to reset it in order for Menu z-index to work
 * Card component comes with a pre-styling dark bgColor, need to override it separately
 */
const DEFAULT_CARD_STYLE = {
  transform: 'none',
  border: '2px solid transparent',
  transition: 'backgroundColor 200ms ease',
  bgColor: 'transparent',
  _dark: { bgColor: 'transparent' },
};

const VERTICAL_CARD_STYLE = {
  minW: '9.875rem', // 158px = thumbnail min width (8.875rem, 142px) + hover padding ("75 (8px)" * 2)
  gap: '75',
  p: '75',
  m: '-8px',
  borderRadius: 'lg',
};

const HORIZONTAL_CARD_STYLE = {
  gap: '200',
  px: '300',
  py: '100',
  borderRadius: 'none',
};

export interface VideoCardLayoutProps
  extends CardProps, Pick<BaseVideoCardProps, 'isReducedMotion' | 'trackStatus'> {}

export const VideoCardLayout = forwardRef<HTMLDivElement, VideoCardLayoutProps>(
  ({ isReducedMotion, trackStatus, ...cardProps }, ref) => {
    const [accentColor, focusAltColor] = useToken('colors', ['accent', 'focus-alt']);
    const playingBorderColor = accentColor !== 'accent' ? accentColor : focusAltColor; // if there is no token `accent`, useToken('colors', 'accent') value will be string "accent"

    const hoverStyle = {
      _hover: isReducedMotion
        ? {} // remove bokeh-iris card hover border animation, this can be removed when we switched to Bokeh-bokeh theme
        : { bgColor: 'fill-component-hover !important' }, // !important is needed as default card style override set bg color already for dark mode
    };

    return (
      <Card
        ref={ref}
        position="static" // Have to be placed here or TS will throw an error
        {...DEFAULT_CARD_STYLE}
        {...(cardProps.direction === 'row' ? HORIZONTAL_CARD_STYLE : VERTICAL_CARD_STYLE)}
        {...(cardProps.onClick && {
          cursor: 'pointer',
          tabIndex: 0,
          _focusVisible: { outlineOffset: '2px', outlineColor: 'focus' },
        })}
        borderColor={trackStatus === 'playing' ? playingBorderColor : 'transparent'}
        {...hoverStyle}
        {...cardProps}
      />
    );
  },
);
